import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "../../../components/ui";
import { FileUpload } from "../../../components/ui";

function getFileType(fileName) {
  const name = (fileName || "").toLowerCase();
  if (name.endsWith(".pdf")) return "PDF";
  if (/\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(name)) return "IMG";
  return "FILE";
}

function getFileTypeColor(type) {
  if (type === "PDF") return "bg-red-100 text-red-800";
  if (type === "IMG") return "bg-blue-100 text-blue-800";
  return "bg-gray-100 text-gray-800";
}

const SortableAttachmentItem = ({ file }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const fileType = getFileType(file.name);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border border-gray-200 rounded-lg p-2 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <span
            className={`px-1.5 py-0.5 text-xs font-semibold rounded ${getFileTypeColor(fileType)}`}
          >
            {fileType}
          </span>
          <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-800">
            Attachment
          </span>
        </div>
        {file.size && (
          <span className="text-xs text-gray-500">
            {(file.size / 1024).toFixed(1)} KB
          </span>
        )}
      </div>
      <p className="text-xs font-medium text-gray-900 truncate" title={file.name}>
        {file.name}
      </p>
    </div>
  );
};

/**
 * Shared attachments section for tickets (RFI, T&M, etc.):
 * cover card, sortable attachment list, file upload + upload button.
 * Consumes state and handlers from useTicketAttachments.
 */
export function TicketAttachmentsSection({
  ticketIdentifier,
  coverSubtitle,
  attachments,
  handleDragEnd,
  pendingFiles,
  pendingFilesHelpText = "Files will be uploaded when you click the upload button below.",
  setPendingFiles,
  isLoadingAttachments,
  isUploadingFiles,
  handleUploadAttachments,
  disabled,
  onUploadError,
  emptyMessage = "No attachments found.",
  addAttachmentsTitle = "Add Attachments",
  uploadButtonText = "Attach Files",
  /** When false, the "Upload N File(s)" button is hidden (e.g. create flow: files upload on save) */
  showUploadButton = true,
  /** When false, the "PDF Pages" section (cover card + sortable list) is hidden (e.g. create modal) */
  showPdfPagesSection = true,
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const coverLabel =
    ticketIdentifier != null
      ? `Cover Page - ${ticketIdentifier}.pdf`
      : "Cover Page.pdf";

  return (
    <>
      {showPdfPagesSection && (
        <div className="">
          <h2 className="text-lg font-semibold text-gray-700">PDF Pages</h2>
          <i className="text-xs text-gray-500">Drag and drop to reorder pages</i>

          <div className="bg-gray-200 border border-gray-300 rounded-lg p-2 shadow-sm mb-2">
            <div className="flex items-start justify-between mb-1">
              <div className="flex items-center gap-1.5">
                <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-800">
                  PDF
                </span>
                {coverSubtitle && (
                  <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-800">
                    {coverSubtitle}
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs font-medium text-gray-900">{coverLabel}</p>
          </div>

          {isLoadingAttachments ? (
            <div className="flex items-center justify-center p-4">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
              <span className="ml-2 text-xs text-gray-600">
                Loading attachments...
              </span>
            </div>
          ) : attachments.length === 0 ? (
            <div className="p-2 bg-gray-50 border border-gray-200 rounded-md">
              <p className="text-xs text-gray-600">{emptyMessage}</p>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={attachments.map((f) => f._id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="grid grid-cols-1 gap-2">
                  {attachments.map((file) => (
                    <SortableAttachmentItem key={file._id} file={file} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      )}

      <div className={showPdfPagesSection ? "border-t pt-6 mt-6" : ""}>
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          {addAttachmentsTitle}
        </h2>
        <FileUpload
          onUploadSuccess={(files) => {
            setPendingFiles((prev) => [...prev, ...files]);
          }}
          onUploadError={(err) => {
            if (onUploadError) onUploadError(err);
          }}
          disabled={disabled}
          buttonText={uploadButtonText}
          buttonVariant="outlineBlue"
          acceptedFileTypes="image/*,application/pdf"
          multiple={true}
          pendingFiles={pendingFiles}
          pendingFilesLabel="Files to attach"
          pendingFilesHelpText={pendingFilesHelpText}
          onRemoveFile={(index) => {
            setPendingFiles((prev) => prev.filter((_, i) => i !== index));
          }}
        />

        {pendingFiles.length > 0 && showUploadButton && (
          <div className="mt-4">
            <Button
              variant="primary"
              text={
                isUploadingFiles
                  ? "Uploading..."
                  : `Upload ${pendingFiles.length} File(s)`
              }
              onClick={() => handleUploadAttachments()}
              disabled={isUploadingFiles}
            />
          </div>
        )}
      </div>
    </>
  );
}
